import dayjs from 'dayjs'
import { get } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { TimedSubtasks } from '../utils/subtasks/extensions/TimedSubtasks'
import { BaseItem } from '../base/BaseItem'
import { Comments } from '../utils/comments'
import { History } from '../utils/history'

/**
 * Class representing a MonthlyReport item, extending the base list item class LstItmBase.
 * Contains additional properties and methods specific to MonthlyReport items.
 *
 * @class MonthlyReport
 * @extends {BaseItem}
 */
export class MonthlyReport extends BaseItem {
	_class = 'MonthlyReport'
	monthly_report_id
	hours_worked
	/**
	 * Creates an instance of MonthlyReport.
	 * @param {string} id - The unique identifier for the MonthlyReport item.
	 */
	constructor(id) {
		super(id)
		this.monthly_report_id = id
		this.subtasks = new TimedSubtasks(this.full.subtasks || [])
		this.comments = new Comments(this.full.comments || [])
		this.history = new History(this.full.history || {})

		makeObservable(this, {
			monthly_report_id: observable,

			hoursWorked: computed,
			setHoursWorked: action.bound
		})
	}

	/**
	 * setSubtasks
	 * @description Sets the subtasks list using `TimedSubtasks` and stores it in the `full` object.
	 * @param {Array} subtasks - An array of subtasks to be set.
	 */
	setSubtasks = (subtasks) => {
		this.subtasks = new TimedSubtasks(subtasks)
		this.chgInFull(['subtasks'], subtasks || [])
	}
	/**
	 * setComments
	 * @description Sets the comments list using the `Comments` class and stores it in the `full` object.
	 * @param {Array} comments - An array of comment objects to be set.
	 */
	setComments = (comments) => {
		this.comments = new Comments(comments)
		this.chgInFull(['comments'], comments)
	}

	/**
	 * get displayName
	 * @description Returns a formatted string representation of the report name as `YYYY-MM`.
	 * @returns {string}
	 */
	get displayName() {
		return dayjs.unix(this.name)?.format('YYYY-MM')
	}

	/**
	 * get hoursWorked
	 * @description Computes the number of worked hours, using `full` or falling back to `compact` data.
	 * @returns {number}
	 */
	get hoursWorked() {
		return get(
			this.full,
			['hours_worked'],
			get(this.compact, ['hours_worked'], 0)
		)
	}

	/**
	 * setHoursWorked
	 * @description Updates the `hours_worked` field in the `full` object.
	 * @param {number} val - The number of hours worked.
	 */
	setHoursWorked = (val) => {
		this.chgInFull(['hours_worked'], val)
	}

	/**
	 * Override updateFromServerCompact to initialize values from server data.
	 * @param {object} compactJson - Compact data from the server.
	 */
	updateFromServerCompact(compactJson) {
		// Set Sprint specific properties (required due to the override)
		if (compactJson.hours_worked) {
			this.setHoursWorked(compactJson.hours_worked)
		}
	}

	/**
	 * Override updateFromServerFull to initialize values from server data.
	 * @param {object} fullJson - Full data from the server.
	 */
	updateFromServerFull(fullJson) {
		if (fullJson.history) {
			this.history = new History(fullJson.history)
		}

		if (fullJson.comments) {
			this.comments = new Comments(fullJson.comments)
		}
		if (fullJson.subtasks) {
			this.subtasks = new TimedSubtasks(fullJson.subtasks)
		}

		// Set Sprint specific properties (required due to the override)
		if (fullJson.hours_worked) {
			this.setHoursWorked(fullJson.hours_worked)
		}
	}
}
